import { BuildingOffice2Icon, LinkSlashIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { UserContext } from '~/app';
import { Pill } from '~/components';
import { Button } from '~/components/buttons/Button/Button';
import { dateStringFormatter, getInitials } from '~/features/utils';
import { ProviderAssociationHistoryEntry } from '~/types';

const TAGS = CT.providerSettings.employerAssociation;

// [MCV-1927] How an association ended, worded for the provider.
const END_REASON_LABELS: Record<ProviderAssociationHistoryEntry['endReason'], string> = {
  EMPLOYER_REMOVED: 'Removed by employer',
  PROVIDER_DISASSOCIATED: 'Ended by you',
};

const historyDate = (value: string) => dateStringFormatter(value, 'SHORT_DATE');

const PreviousEmployers = ({
  history,
  failed,
}: {
  history: ProviderAssociationHistoryEntry[] | null | undefined;
  failed: boolean | undefined;
}) => (
  <div data-cy={TAGS.historySection} className="mt-6">
    <h4 className="mb-3 text-sm font-semibold text-content-primary">Previous Employers</h4>
    {failed && !history?.length ? (
      <p
        data-cy={TAGS.historyError}
        className="rounded-lg bg-base px-5 py-4 text-[13px] text-content-secondary"
      >
        Previous employers could not be loaded.
      </p>
    ) : !history?.length ? (
      <p
        data-cy={TAGS.historyEmpty}
        className="rounded-lg bg-base px-5 py-4 text-[13px] text-content-secondary"
      >
        No previous employers.
      </p>
    ) : (
      <>
        {failed && (
          // A reload failed: the list below is what loaded before and may be out of date.
          <p data-cy={TAGS.historyStale} className="mb-2 text-xs text-content-secondary">
            Couldn&apos;t refresh previous employers. This list may be out of date.
          </p>
        )}
        <ul className="divide-y divide-grey-900 rounded-lg border border-grey-900 bg-white">
          {history.map((entry) => (
            <li
              key={entry.id}
              data-cy={TAGS.historyRow}
              className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6"
            >
              <div className="min-w-0">
                <p
                  data-cy={TAGS.historyEmployerName}
                  className="break-words font-medium text-content-primary"
                >
                  {entry.employerName}
                </p>
                <p className="text-xs text-content-secondary">
                  <span data-cy={TAGS.historyAssociatedDate}>
                    Associated {historyDate(entry.associatedAt)}
                  </span>
                  {' - '}
                  <span data-cy={TAGS.historyEndedDate}>Ended {historyDate(entry.endedAt)}</span>
                </p>
              </div>
              <p
                data-cy={TAGS.historyEndReason}
                className="text-xs font-medium text-content-secondary sm:shrink-0"
              >
                {END_REASON_LABELS[entry.endReason] ?? entry.endReason}
              </p>
            </li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export const EmployerAssociationSection = () => {
  const userRef = UserContext.useActorRef();
  const { vaultRef } = useSelector(userRef, (state) => state.context);

  const employerAssociationRef = useSelector(vaultRef, (s) => s?.context.employerAssociationRef);
  const association = useSelector(employerAssociationRef, (s) => s?.context.association);
  const history = useSelector(employerAssociationRef, (s) => s?.context.history);
  const historyFailed = useSelector(employerAssociationRef, (s) => s?.context.historyFailed);
  const isLoading = useSelector(employerAssociationRef, (s) => s?.matches('$_LOADING'));

  useEffect(() => {
    if (employerAssociationRef) {
      employerAssociationRef.send({ type: 'ON_LOAD' });
    }
  }, [employerAssociationRef]);

  if (!employerAssociationRef) return null;

  const handleDisassociate = () => {
    if (!association) return;
    employerAssociationRef.send({ type: 'ON_SEND_DISASSOCIATE_CONFIRMATION' });
  };

  // [MCV-1927] A reload (after disassociating, or a websocket message) keeps the card up once
  // the history has answered, so the list does not blink out.
  if (isLoading && history == null && !historyFailed) return null;

  return (
    <div data-cy={TAGS.section} className="mt-6 rounded-lg bg-white p-7 text-left shadow-lg">
      <div className="mb-1.5 flex items-center gap-2.5">
        <BuildingOffice2Icon className="h-5 w-5 text-primary-500" />
        <h3 className="font-semibold text-content-primary">Employer Association</h3>
      </div>
      <p className="mb-5 text-[13px] leading-relaxed text-content-secondary">
        Your employer association allows an organization to view and manage your credentialing vault
        on your behalf.
      </p>

      {association ? (
        <div className="rounded-lg border border-grey-900 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-700 text-sm font-bold text-white">
                {getInitials(association.employer.employerName ?? '')}
              </div>
              <div className="min-w-0">
                <p
                  data-cy={TAGS.employerName}
                  className="break-words font-medium text-content-primary"
                >
                  {association.employer.employerName}
                </p>
                <p data-cy={TAGS.associatedDate} className="text-xs text-content-secondary">
                  Associated since {dateStringFormatter(association.associatedAt, 'LONG_DATE')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Pill label="Active" type="success" cyTag={TAGS.status} />
              <Button
                cyTag={TAGS.disassociateBtn}
                type="button"
                colorVariant="error"
                // A reload keeps the card up, but $_LOADING ignores the confirmation event.
                disabled={isLoading}
                onClick={handleDisassociate}
              >
                <span className="inline-flex items-center gap-2">
                  <LinkSlashIcon className="h-4 w-4" />
                  Disassociate
                </span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          data-cy={TAGS.noAssociation}
          className="flex items-center gap-3 rounded-lg bg-base px-5 py-4 text-[13px] text-content-secondary"
        >
          <BuildingOffice2Icon className="h-5 w-5 shrink-0 text-content-tertiary" />
          You are not currently associated with any employer.
        </div>
      )}

      <PreviousEmployers history={history} failed={historyFailed} />
    </div>
  );
};
